"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, ExternalLink, Cpu, Code, Smartphone, BrainCircuit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TransformYourVision() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const isDesktop = window.matchMedia("(pointer: fine)").matches;
    if (!isDesktop) return;
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX / window.innerWidth * 20 - 10,
        y: e.clientY / window.innerHeight * 20 - 10
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);
  return <section data-section-id="36558" id="instant-intelligence" ref={sectionRef} className="dark bg-background text-foreground relative min-h-[90vh] flex items-center overflow-hidden py-24 px-4 sm:px-8">
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(210,100%,65%,0.15),transparent_70%)]" />
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeOpacity="0.05" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
        <div className={`lg:col-span-7 flex flex-col gap-6 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h1 className="font-default text-5xl md:text-[5rem] font-bold leading-[1.1] tracking-tighter text-white">
            Full-scale AI capability{" "}
            <span className="bg-gradient-to-r from-white to-primary bg-clip-text text-transparent">
              in under a minute
            </span>
          </h1>

          <p className="font-default text-lg md:text-xl text-muted-foreground leading-[1.7] max-w-[700px]">
            OceanClaw lets anyone unlock powerful AI through a private iMessage or Slack conversation immediately after checkout, without dealing with apps, APIs, prompts, or complicated setup.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl px-8 py-6 text-base font-bold shadow-[0_0_30px_hsl(210,100%,65%,0.4)] transition-all hover:shadow-[0_0_40px_hsl(210,100%,65%,0.6)]">
              <Link to="#">
                Start Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>

            <Button asChild variant="outline" className="bg-transparent border-border text-muted-foreground hover:bg-secondary/10 rounded-xl px-8 py-6 text-base font-semibold transition-all">
              <Link to="#">
                Explore Solutions
                <ExternalLink className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        <div className={`lg:col-span-5 relative flex justify-center items-center transition-all duration-1000 delay-300 transform ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
          <div className="absolute inset-0 bg-primary/20 blur-[100px] rounded-full transform scale-75" />

          <div className="relative w-full max-w-full lg:max-w-[500px] aspect-square" style={{
          transform: `perspective(1000px) rotateY(${mousePosition.x}deg) rotateX(${-mousePosition.y}deg)`,
          transition: "transform 0.1s ease-out"
        }}>
            <svg viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-2xl">
              <rect x="50" y="100" width="400" height="300" rx="20" fill="hsl(220 10% 8%)" stroke="hsl(210 100% 65%)" strokeWidth="2" className="animate-[pulse_4s_ease-in-out_infinite]" />
              
              <path d="M100 400 L100 350 L150 300 L350 300 L400 250" stroke="hsl(210 100% 65%)" strokeWidth="3" strokeLinecap="round" className="opacity-60" />
              <path d="M400 100 L400 150 L350 200 L150 200 L100 250" stroke="hsl(210 100% 65%)" strokeWidth="3" strokeLinecap="round" className="opacity-60" />
              
              <circle r="4" fill="#fff">
                <animateMotion dur="3s" repeatCount="indefinite" path="M100 400 L100 350 L150 300 L350 300 L400 250" />
              </circle>
              <circle r="4" fill="#fff">
                <animateMotion dur="4s" repeatCount="indefinite" path="M400 100 L400 150 L350 200 L150 200 L100 250" />
              </circle>

              <g transform="translate(220, 220)" style={{
              transformBox: "fill-box",
              transformOrigin: "center",
              willChange: "transform"
            }}>
                <rect x="-30" y="-30" width="60" height="60" rx="10" fill="hsl(210 100% 65%)" fillOpacity="0.2" stroke="hsl(210 100% 65%)" />
                <Cpu stroke="currentColor" className="text-primary w-10 h-10 -translate-x-5 -translate-y-5" />
              </g>

              <g className="animate-[bounce_3s_infinite]" style={{
              transformBox: "fill-box",
              transformOrigin: "center",
              willChange: "transform"
            }}>
                <rect x="120" y="150" width="50" height="50" rx="8" fill="hsl(220 8% 12%)" stroke="hsl(210 100% 65%)" />
                <Code stroke="currentColor" className="text-white w-6 h-6 x-132 y-162 translate-x-[133px] translate-y-[163px]" />
              </g>

              <g className="animate-[bounce_4s_infinite_1s]" style={{
              transformBox: "fill-box",
              transformOrigin: "center",
              willChange: "transform"
            }}>
                <rect x="330" y="150" width="50" height="50" rx="8" fill="hsl(220 8% 12%)" stroke="hsl(210 100% 65%)" />
                <Smartphone stroke="currentColor" className="text-white w-6 h-6 translate-x-[343px] translate-y-[163px]" />
              </g>

              <g className="animate-[bounce_3.5s_infinite_0.5s]" style={{
              transformBox: "fill-box",
              transformOrigin: "center",
              willChange: "transform"
            }}>
                <rect x="225" y="320" width="50" height="50" rx="8" fill="hsl(220 8% 12%)" stroke="hsl(210 100% 65%)" />
                <BrainCircuit stroke="currentColor" className="text-white w-6 h-6 translate-x-[238px] translate-y-[333px]" />
              </g>

              <circle cx="60" cy="110" r="3" fill="hsl(210 100% 65%)" />
              <circle cx="440" cy="110" r="3" fill="hsl(210 100% 65%)" />
              <circle cx="60" cy="390" r="3" fill="hsl(210 100% 65%)" />
              <circle cx="440" cy="390" r="3" fill="hsl(210 100% 65%)" />
            </svg>
          </div>
        </div>
      </div>
    </section>;
}